import { type IPdfBlock } from '@mahawi/eshop-common/dist/src/types';

import {
  FALLBACK_LANGUAGE,
  type ILegalBlock,
  interpolate,
  type ITermsSection,
  resolveBlock,
  type TInterpolationValues,
} from './terms-content';

export interface IPrivacyPurpose {
  id: string;
  title: string;
  data: string;
  legalBasis: string;
  retention: string;
}

interface IPrivacyPurposeLabels {
  data: string;
  legalBasis: string;
  retention: string;
}

export interface IPrivacySection extends ITermsSection {
  purposes?: IPrivacyPurpose[];
}

export interface IPrivacyContent {
  title: string;
  subtitle: string;
  versionLabel: string;
  purposeLabels: IPrivacyPurposeLabels;
  sections: IPrivacySection[];
}

export interface IPrivacyResourceReader {
  getResource: (
    languageCode: string,
    namespace: string,
    key: string,
  ) => IPrivacyContent | undefined;
}

const isPrivacyContent = (
  value: IPrivacyContent | undefined,
): value is IPrivacyContent =>
  Boolean(
    value &&
    Array.isArray(value.sections) &&
    value.sections.length > 0 &&
    value.purposeLabels,
  );

export const readPrivacyContent = (
  reader: IPrivacyResourceReader,
  languageCode: string,
  namespace: string,
): IPrivacyContent | null => {
  const localized: IPrivacyContent | undefined = reader.getResource(
    languageCode,
    namespace,
    'privacy',
  );

  if (isPrivacyContent(localized)) {
    return localized;
  }

  const fallback: IPrivacyContent | undefined = reader.getResource(
    FALLBACK_LANGUAGE,
    namespace,
    'privacy',
  );

  return isPrivacyContent(fallback) ? fallback : null;
};

const resolvePurpose = (
  purpose: IPrivacyPurpose,
  values: TInterpolationValues,
): IPrivacyPurpose => ({
  id: purpose.id,
  title: interpolate(purpose.title, values),
  data: interpolate(purpose.data, values),
  legalBasis: interpolate(purpose.legalBasis, values),
  retention: interpolate(purpose.retention, values),
});

export const resolvePrivacyContent = (
  content: IPrivacyContent,
  values: TInterpolationValues,
): IPrivacyContent => ({
  title: interpolate(content.title, values),
  subtitle: interpolate(content.subtitle, values),
  versionLabel: interpolate(content.versionLabel, values),
  purposeLabels: content.purposeLabels,
  sections: content.sections.map(
    (section: IPrivacySection): IPrivacySection => ({
      id: section.id,
      heading: interpolate(section.heading, values),
      blocks: section.blocks.map((block: ILegalBlock): IPdfBlock =>
        resolveBlock(block, values),
      ),
      ...(section.purposes === undefined
        ? {}
        : {
            purposes: section.purposes.map(
              (purpose: IPrivacyPurpose): IPrivacyPurpose =>
                resolvePurpose(purpose, values),
            ),
          }),
    }),
  ),
});
